import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "wouter";
import { motion } from "framer-motion";
import { CheckCircle, Clock, ExternalLink, Pencil, XCircle } from "lucide-react";
import { getEventType } from "@shared/eventTypes";
import { parseUTCDate } from "@/utils/dateUtils";
import type { AdminHostEvent, HostEventReviewStatus } from "../api/useAdminHostEvents";

export function ReviewBadge({ status }: { status: HostEventReviewStatus }) {
  if (status === "approved")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/15 text-emerald-500">
        <CheckCircle className="w-3 h-3" /> Approved
      </span>
    );
  if (status === "rejected")
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/15 text-red-400">
        <XCircle className="w-3 h-3" /> Rejected
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400">
      <Clock className="w-3 h-3" /> Pending
    </span>
  );
}

export function CoverThumb({ src }: { src: string }) {
  const ref = useRef<HTMLImageElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const show = () => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + window.scrollY + 8, left: r.left + window.scrollX });
  };
  return (
    <div className="shrink-0">
      <img
        ref={ref}
        src={src}
        alt=""
        className="w-14 h-14 rounded-xl object-cover bg-dark-border cursor-pointer"
        onMouseEnter={show}
        onMouseLeave={() => setPos(null)}
      />
      {pos && createPortal(
        <img
          src={src}
          alt=""
          style={{ position: "absolute", top: pos.top, left: pos.left, zIndex: 9999 }}
          className="w-[480px] h-auto rounded-xl shadow-2xl border border-dark-border pointer-events-none"
        />,
        document.body,
      )}
    </div>
  );
}

export function AdminHostEventRow({
  event,
  onReview,
}: {
  event: AdminHostEvent;
  onReview: (id: string, s: HostEventReviewStatus) => void;
}) {
  const [, navigate] = useLocation();
  const parsed = parseUTCDate(event.date);
  const dateLabel = `${parsed.weekday}, ${parsed.monthShort} ${parsed.day}`;
  const ownerName = [event.ownerFirstName, event.ownerLastName].filter(Boolean).join(" ") || "Unknown";
  const types = (event.types ?? []) as string[];

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="card flex flex-col gap-3">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          {event.coverImageUrl ? (
            <CoverThumb src={event.coverImageUrl} />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-dark-card shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-white leading-snug">{event.name}</h3>
              <ReviewBadge status={event.reviewStatus} />
            </div>
            <p className="text-sm text-dark-muted mt-0.5">
              {dateLabel} · {event.time} · {event.venueName}
              {event.cityName ? `, ${event.cityName}` : ""}
            </p>
            <p className="text-xs text-dark-muted mt-0.5">
              Host: {ownerName}
              {event.ownerEmail ? ` (${event.ownerEmail})` : ""}
              {event.groupName ? ` · Group: ${event.groupName}` : ""}
            </p>
            {event.source && (
              <p className="text-xs text-dark-muted mt-0.5 font-mono truncate" title={event.source}>
                {event.source}
              </p>
            )}
            {event.description && (
              <p
                className="text-xs text-dark-muted mt-1.5 line-clamp-3"
                dangerouslySetInnerHTML={{ __html: event.description }}
              />
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {event.slug && (
            <>
              <button
                type="button"
                onClick={() => navigate(`/events/${event.slug}/edit`)}
                className="btn btn-ghost btn-sm p-2"
                title="Edit event"
              >
                <Pencil className="w-4 h-4" />
              </button>
              <a
                href={`/events/${event.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-ghost btn-sm p-2"
                title="View public page"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        {types.slice(0, 4).map((t) => {
          const et = getEventType(t);
          return (
            <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-dark-card text-dark-muted">
              {et ? `${et.emoji} ${et.label}` : t}
            </span>
          );
        })}
        {event.rsvpCount > 0 && (
          <span className="text-xs text-dark-muted">{event.rsvpCount} RSVP{event.rsvpCount !== 1 ? "s" : ""}</span>
        )}
        {event.capacity != null && (
          <span className="text-xs text-dark-muted">Cap: {event.capacity}</span>
        )}
        {event.priceCents != null && (
          <span className="text-xs text-dark-muted">
            {event.priceCents === 0 ? "Free" : `$${(event.priceCents / 100).toFixed(2)}`}
          </span>
        )}
        <span className="text-xs text-dark-muted ml-auto">
          Updated {new Date(event.updatedAt).toLocaleDateString()}
        </span>
      </div>

      <div className="flex items-center gap-2 pt-1 border-t border-dark-border">
        {event.reviewStatus !== "approved" && (
          <button
            onClick={() => onReview(event.id, "approved")}
            className="btn btn-sm bg-emerald-600/20 hover:bg-emerald-600/30 text-emerald-400 border-0"
          >
            <CheckCircle className="w-3.5 h-3.5 mr-1.5" /> Approve
          </button>
        )}
        {event.reviewStatus !== "rejected" && (
          <button
            onClick={() => onReview(event.id, "rejected")}
            className="btn btn-sm bg-red-600/20 hover:bg-red-600/30 text-red-400 border-0"
          >
            <XCircle className="w-3.5 h-3.5 mr-1.5" /> Reject
          </button>
        )}
        {event.reviewStatus !== "pending" && (
          <button
            onClick={() => onReview(event.id, "pending")}
            className="btn btn-sm btn-ghost text-dark-muted"
          >
            <Clock className="w-3.5 h-3.5 mr-1.5" /> Reset to pending
          </button>
        )}
      </div>
    </motion.div>
  );
}
